import React, { CSSProperties, FC, Fragment, useMemo } from 'react'
import Head from 'next/head'

import config from '../../common/config'
import HeadTransport, { HrefLangTransport } from '../../transports/common/head-transport'
import { useTranslation } from '../../hooks/locale'
import { getBasePath } from '../../utils/miscellaneous-utils'
import PageHeadTransport, { MetaTagTransport } from '../../transports/common/page-head-transport'
import type { ImagePreloadLink } from '../../common/types'

type Props = {
    head: HeadTransport
    eventApplicationName?: boolean
    pageHead?: PageHeadTransport
    imagePreloadLinks?: ImagePreloadLink[]
    preconnectDomains?: string[]
    children?: React.ReactNode
}

const _getHrefLangItems = (hrefLangObject: HrefLangTransport | null | undefined) =>
    hrefLangObject
        ? Object.entries(hrefLangObject)
              .filter(([_, href]) => href !== null)
              .map(([lang, href]) => <link key={lang} rel="alternate" href={href} hrefLang={lang} />)
        : []

const _getTagManagerHTML = (containerId: string) => ({
    __html: `
        window.dataLayer = window.dataLayer || [];
        function gtag(){dataLayer.push(arguments);}

        if (localStorage.getItem('consentMode') === null) {
            gtag('consent', 'default', {
                'ad_storage': 'denied',
                'ad_user_data': 'denied',
                'ad_personalization': 'denied',
                'analytics_storage': 'denied',
                'functionality_storage': 'denied',
                'personalization_storage': 'denied',
                'security_storage': 'denied',
            });
        } else {
            gtag('consent', 'default', JSON.parse(localStorage.getItem('consentMode')));
        }

        (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
        new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
        j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.defer=true;j.src=
        'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
        })(window,document,'script','dataLayer','${containerId}');`,
})

const _style: CSSProperties = { display: 'none', visibility: 'hidden' }

const Page: FC<Props> = ({
    head: { title, copyright, socialMediaImage, googleSiteVerification, description, locale, hreflang, canonical },
    eventApplicationName,
    pageHead,
    imagePreloadLinks,
    preconnectDomains,
    children,
}) => {
    const _hrefLangItems = useMemo(() => _getHrefLangItems(hreflang), [hreflang])
    const _tagManagerHtml = useMemo(() => _getTagManagerHTML(config.gtmContainerId), [])
    const { t } = useTranslation()
    const _imagePreloadItems = useMemo(() => {
        const items =
            imagePreloadLinks?.map((imageLink, index) => {
                const linkProps: Record<string, string> = {
                    rel: 'preload',
                    as: imageLink.as || 'image',
                }

                // Use either imagesrcset or href, but not both
                if (imageLink.imagesrcset) {
                    linkProps.imagesrcset = imageLink.imagesrcset
                } else if (imageLink.href) {
                    linkProps.href = imageLink.href
                }

                if (imageLink.imagesizes) {
                    linkProps.imagesizes = imageLink.imagesizes
                }

                if (imageLink.type) {
                    linkProps.type = imageLink.type
                }

                if (imageLink.fetchPriority) {
                    linkProps.fetchPriority = imageLink.fetchPriority
                }

                if (imageLink.media) {
                    linkProps.media = imageLink.media
                }

                return <link key={`image-preload-${index}`} {...linkProps} />
            }) || []

        return items
    }, [imagePreloadLinks])

    const { linkTags, headTitle, otherMetaTags, metaDescription } = useMemo(() => {
        const metaTags = pageHead?.metaTags
        const linkTags = pageHead?.linkTags
        const headTitle = pageHead?.title ?? title

        const { otherMetaTags, descriptionContent } = (metaTags ?? []).reduce(
            (meta, tag) => {
                if (tag?.name?.toLowerCase() === 'description') {
                    meta.descriptionContent = tag.content
                } else {
                    meta.otherMetaTags.push(tag)
                }
                return meta
            },
            { otherMetaTags: [] as MetaTagTransport[], descriptionContent: undefined as string | undefined },
        )

        const metaDescription = descriptionContent ?? description

        return { linkTags, headTitle, otherMetaTags, metaDescription }
    }, [pageHead, title, description])

    return (
        <Fragment>
            <Head>
                {/* Title */}
                <title>{headTitle}</title>

                {/* Essential Meta Tags - Core Document Information */}
                <meta httpEquiv="X-UA-Compatible" content="IE=edge" />
                <meta
                    name="viewport"
                    content="viewport-fit=cover, width=device-width, initial-scale=1.0, minimum-scale=1.0, maximum-scale=5.0"
                />
                {!!description && <meta name="description" content={metaDescription} />}
                {!!locale && <meta name="content-language" content={locale} />}

                {/* Site Verification Meta Tags */}
                <meta name="msvalidate.01" content="7295756D5DACC26A911F2500200504FC" />
                {!!googleSiteVerification && <meta name="google-site-verification" content={googleSiteVerification} />}
                <meta name="p:domain_verify" content="4bd5af6331242b3a140bbcdc4391e5c6" />
                {locale === 'de-AT' && (
                    <meta name="audisto-confirm" content="c13def876fddd445ca7cc039b06efe84e2ae1679" />
                )}

                {/* SEO Meta Tags */}
                <meta
                    name="robots"
                    content={
                        (canonical?.noIndex ? 'noindex, ' : 'index, ') + (canonical?.noFollow ? 'nofollow' : 'follow')
                    }
                />
                {!!copyright && <meta name="copyright" content={copyright} />}

                {/* Dynamic Meta Tags from PageHead */}
                {otherMetaTags?.map((metaTag, index) => {
                    const key = metaTag.name || metaTag.property || index
                    return <meta key={key} {...metaTag} />
                })}

                {/* Open Graph Meta Tags */}
                {!!canonical && !!canonical.url && (
                    <Fragment>
                        <meta property="og:title" content={title || ''} />
                        <meta property="og:description" content={description || ''} />
                        <meta property="og:site_name" content={t('companyShort')} />
                        <meta property="og:type" content="website" />
                        <meta property="og:url" content={canonical.url} />
                        <meta property="og:locale" content={locale || ''} />
                        {!!socialMediaImage && <meta property="og:image" content={socialMediaImage} />}
                    </Fragment>
                )}

                {/* Twitter Meta Tags */}
                {!!canonical && !!canonical.url && (
                    <Fragment>
                        <meta name="twitter:card" content="summary" />
                        <meta name="twitter:title" content={title || ''} />
                        <meta name="twitter:description" content={description || ''} />
                        <meta name="twitter:url" content={canonical.url} />
                        <meta name="twitter:site" content="@traumfewo" />
                        {!!socialMediaImage && <meta name="twitter:image" content={socialMediaImage} />}
                    </Fragment>
                )}

                {/* App Meta Tags */}
                <meta
                    name="application-name"
                    content={
                        eventApplicationName
                            ? 'Traum-Ferienwohnungen - Aktivitäten, Touren & Attraktionen'
                            : 'Traum-Ferienwohnungen - Ferien mit Herz'
                    }
                />
                <meta name="mobile-web-app-capable" content="yes" />
                <meta name="apple-mobile-web-app-title" content={t('companyShorter')} />

                {/* Microsoft Application Meta Tags */}
                <meta name="msapplication-TileColor" content="#FFFFFF" />
                <meta name="msapplication-TileImage" content={getBasePath('/assets/images/favicon/favicon-70.png')} />
                <meta name="msapplication-TileImage" content={getBasePath('/assets/images/favicon/favicon-144.png')} />
                <meta name="msapplication-TileImage" content={getBasePath('/assets/images/favicon/favicon-150.png')} />
                <meta name="theme-color" content="#ffffff" />

                {/* Resource Hints - Preconnect for Critical Resources */}
                {preconnectDomains?.map(domain => (
                    <link key={domain} rel="preconnect" href={domain} />
                ))}
                <link rel="preconnect" href="https://img2.traum-ferienwohnungen.de" />
                <link rel="preconnect" href="https://images.traum-ferienwohnungen.de" />
                <link rel="preconnect" href={config.mediaBaseUrl} />

                {/* Resource Hints - DNS Prefetch for Non-Critical Resources */}
                <link rel="dns-prefetch" href="//storage.googleapis.com" />
                <link rel="dns-prefetch" href="//www.googletagmanager.com" />

                {/* SEO Links - Canonical, Next, Prev */}
                {!!canonical?.url && <link rel="canonical" href={canonical.url} />}
                {!!canonical?.relNext && <link rel="next" href={canonical.relNext} />}
                {!!canonical?.relPrev && <link rel="prev" href={canonical.relPrev} />}

                {/* Hreflang Links */}
                {_hrefLangItems}

                {/* Dynamic Link Tags from PageHead */}
                {linkTags?.map((linkTag, index) => {
                    const key = linkTag.rel || linkTag.href || index
                    return <link key={key} {...linkTag} />
                })}

                {/* Critical Resource Preloads */}
                {_imagePreloadItems}

                {/* Favicon and Icon Links */}
                <link
                    rel="shortcut icon"
                    type="image/x-icon"
                    href={getBasePath('/assets/images/favicon/favicon.ico')}
                />
                <link
                    rel="icon"
                    type="image/vnd.microsoft.icon"
                    sizes="16x16 32x32 48x48"
                    href={getBasePath('/assets/images/favicon/favicon.ico')}
                />
                <link
                    rel="icon"
                    type="image/png"
                    sizes="16x16"
                    href={getBasePath('/assets/images/favicon/favicon-16.png')}
                />
                <link
                    rel="icon"
                    type="image/png"
                    sizes="32x32"
                    href={getBasePath('/assets/images/favicon/favicon-32.png')}
                />
                <link
                    rel="apple-touch-icon"
                    sizes="76x76"
                    href={getBasePath('/assets/images/favicon/favicon-76.png')}
                />
                <link
                    rel="apple-touch-icon"
                    sizes="120x120"
                    href={getBasePath('/assets/images/favicon/favicon-120.png')}
                />
                <link
                    rel="apple-touch-icon"
                    sizes="152x152"
                    href={getBasePath('/assets/images/favicon/favicon-152.png')}
                />
                <link
                    rel="apple-touch-icon"
                    sizes="180x180"
                    href={getBasePath('/assets/images/favicon/favicon-180.png')}
                />
                <link
                    rel="apple-touch-icon"
                    sizes="192x192"
                    href={getBasePath('/assets/images/favicon/favicon-180.png')}
                />
                <link
                    rel="mask-icon"
                    href={getBasePath('/assets/images/favicon/safari-pinned-tab.svg')}
                    color="#5bbad5"
                />

                {/* Scripts */}
                <script dangerouslySetInnerHTML={_tagManagerHtml} />
            </Head>

            {/* Google Tag Manager (noscript) */}
            <noscript>
                <iframe
                    src={`https://www.googletagmanager.com/ns.html?id=${config.gtmContainerId}`}
                    height="0"
                    width="0"
                    style={_style}
                />
            </noscript>
            {/* End Google Tag Manager (noscript) */}

            {children}
        </Fragment>
    )
}

export default Page
